import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://xentrios.com' },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://xentrios.com/compare' },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'RevTech vs Dental Intelligence',
          item: 'https://xentrios.com/compare/revtech-vs-dental-intelligence',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the core difference between RevTech and Dental Intelligence?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dental Intelligence is primarily a production analytics and patient engagement platform — it helps practices understand scheduling, case acceptance, and patient recall. RevTech is a revenue cycle intelligence platform — it focuses on what happens after the patient visit: claim submission, denial management, payer performance, and payment reconciliation. The two products solve different problems and can complement each other in a mature DSO technology stack.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use both Dental Intelligence and RevTech together?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. The NDS / RevTech / Xentrios stack is designed to work alongside existing tools. RevTech addresses the RCM layer (post-claim) while Dental Intelligence addresses the production layer (pre-claim and patient engagement). The NDS DataVault can normalize data from both, giving your team a unified view of production and collections performance in a single data warehouse.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does RevTech have any production analytics like Dental Intelligence?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'RevTech includes InsightFlow, which provides enterprise AR analytics and net collection rate tracking by location, payer, and provider. InsightFlow is not a patient scheduling or case acceptance tool, but it does bridge production data and financial outcomes. For groups that want both production and RCM analytics in one stack, Xentrios PMS + InsightFlow covers both layers natively.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which platform is better for a DSO that is growing through acquisition?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For acquisition-stage DSOs, revenue cycle control is typically the higher-priority problem — newly acquired locations often have inconsistent billing practices, elevated denial rates, and AR aging issues that compound quickly at scale. RevTech addresses those problems directly. Dental Intelligence is better suited to the operational optimization phase after the revenue cycle baseline has been stabilized.',
          },
        },
      ],
    },
  ],
};

const tableRows = [
  {
    criterion: 'Denial management',
    revtech: { value: 'Full — DenialIQ with CARC/RARC intelligence, AI appeal generation, root cause analytics', check: true },
    competitor: { value: 'Not a core product focus', check: false },
  },
  {
    criterion: 'Payment reconciliation',
    revtech: { value: 'Full — ReconIQ matches ERA/EOB to claims, identifies short-pays and adjustments', check: true },
    competitor: { value: 'Not included', check: false },
  },
  {
    criterion: 'Payer contract analytics',
    revtech: { value: 'PayerIQ benchmarks contracted rates vs. actual reimbursements across all U.S. dental payers', check: true },
    competitor: { value: 'Not a core feature', check: false },
  },
  {
    criterion: 'Multi-location AR roll-up',
    revtech: { value: 'InsightFlow provides enterprise AR aging across all locations, payers, and providers', check: true },
    competitor: { value: 'Limited — production roll-up is strong, AR-specific roll-up is not the focus', check: false },
  },
  {
    criterion: 'Patient engagement features',
    revtech: { value: 'Not included in RevTech; available via Xentrios PMS layer', check: false },
    competitor: { value: 'Strong — recall, reactivation, two-way texting, and case acceptance tools', check: true },
  },
  {
    criterion: 'Production analytics',
    revtech: { value: 'InsightFlow covers net collection rate and financial production; scheduling analytics via Xentrios', check: false },
    competitor: { value: 'Strong — provider production, case acceptance, scheduling analytics are core', check: true },
  },
  {
    criterion: 'Clearinghouse integration',
    revtech: { value: 'ClaimVision integrates with major U.S. clearinghouses for pre-submission scrubbing and status tracking', check: true },
    competitor: { value: 'Not a core capability', check: false },
  },
  {
    criterion: 'Native PMS integration',
    revtech: { value: 'Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, Xentrios via NDS DataVault', check: true },
    competitor: { value: 'Dentrix, Eaglesoft, Open Dental, CareStack, Denticon, and others', check: true },
  },
  {
    criterion: 'Data warehouse layer',
    revtech: { value: 'NDS DataVault — full ETL, normalization, and data warehouse for analytics and reporting', check: true },
    competitor: { value: 'In-platform analytics only; no standalone data warehouse offering', check: false },
  },
  {
    criterion: 'Pricing model',
    revtech: { value: 'Module-based; scales with locations and product selection', check: true },
    competitor: { value: 'Per-location SaaS subscription', check: true },
  },
];

export default function RevTechVsDentalIntelligencePage() {
  const cmsPage = useCmsPage('xentrios', 'revtech-vs-dental-intelligence');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'RevTech vs. Dental Intelligence');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Both platforms serve dental groups, but they solve different problems. Dental Intelligence focuses on production analytics and patient engagement. RevTech focuses on revenue cycle intelligence — claims, denials, payer performance, and reconciliation. Here is an objective look at each.');
  const tableRowItems = c(cmsPage, 'features', 'items', tableRows);
  return (
    <div>
      <Helmet>
        <title>RevTech vs Dental Intelligence — Dental RCM Analytics Comparison</title>
        <meta
          name="description"
          content="Compare RevTech Partners vs. Dental Intelligence for dental RCM analytics. Understand the differences in product scope, payer intelligence, denial management, and DSO-specific capabilities."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Platform Comparison</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              See RevTech in Action
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              Explore the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* Intro framing */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Market Context</p>
          <h2>Two strong platforms. Two different problems.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            The dental DSO technology market has split into two distinct layers: the production layer (scheduling,
            patient engagement, case acceptance) and the revenue cycle layer (claims, denials, payer performance,
            reconciliation). Understanding which layer is your primary problem determines which platform belongs in
            your stack.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div style={{ background: '#f8f9fc', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Dental Intelligence</h3>
              <p>
                Dental Intelligence is a well-established analytics platform that gives dental practices and
                small-to-mid-size groups visibility into production metrics. Its core strengths are patient
                recall and reactivation tools, case acceptance tracking, and scheduling analytics. If your
                primary challenge is understanding why production varies across providers or keeping
                the schedule full, Dental Intelligence is a proven solution in that category.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Where Dental Intelligence is not focused: the revenue cycle events that happen after the
                patient appointment. Claim submission quality, denial root causes, payer contract performance,
                and payment reconciliation are not its core product surface.
              </p>
            </div>
            <div style={{ background: '#f0f4ff', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>RevTech Partners</h3>
              <p>
                RevTech Partners is an RCM intelligence platform purpose-built for U.S. dental DSOs. Its five
                products — InsightFlow (AR analytics), DenialIQ (denial management), ClaimVision (pre-submission
                intelligence), PayerIQ (payer contract analytics), and ReconIQ (payment reconciliation) — cover
                every phase of the post-visit revenue cycle.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Where RevTech is not focused: patient scheduling workflows, appointment reminders, or two-way
                patient communication. Those functions live in the Xentrios PMS layer for groups using the
                full NDS / RevTech / Xentrios stack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Side-by-Side Comparison</p>
          <h2>RevTech vs. Dental Intelligence — Feature Comparison</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Evaluated across 10 criteria relevant to DSO revenue cycle and analytics decisions.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem', minWidth: '640px' }}>
              <thead>
                <tr style={{ background: 'var(--xen-navy)', color: '#fff' }}>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>Criterion</th>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>RevTech Partners</th>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>Dental Intelligence</th>
                </tr>
              </thead>
              <tbody>
                {tableRowItems.map((row, i) => (
                  <tr key={row.criterion} style={{ background: i % 2 === 0 ? '#f8f9fc' : '#fff' }}>
                    <td style={{ padding: '0.85rem 1.1rem', fontWeight: 600, color: 'var(--xen-navy)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {row.criterion}
                    </td>
                    <td style={{ padding: '0.85rem 1.1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.4rem', color: row.revtech.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.revtech.check ? '✓' : '✗'}
                      </span>
                      {row.revtech.value}
                    </td>
                    <td style={{ padding: '0.85rem 1.1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.4rem', color: row.competitor.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.competitor.check ? '✓' : '✗'}
                      </span>
                      {row.competitor.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Verdict */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Verdict</p>
          <h2>Which platform is right for your group?</h2>
          <div className="xen-two-col" style={{ gap: '2.5rem', marginTop: '2rem' }}>
            <div style={{ background: '#fff', border: '2px solid #e0e4f0', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Choose Dental Intelligence if…</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, color: '#444' }}>
                <li>Your primary challenge is production variability across providers or locations</li>
                <li>Patient recall, reactivation, and case acceptance are your focus areas</li>
                <li>You want a scheduling-to-production analytics loop with patient communication tools</li>
                <li>Your RCM processes are already well-managed and your denial rates are at or below benchmark</li>
                <li>You're a small-to-mid-size group where production analytics has the highest ROI</li>
              </ul>
            </div>
            <div style={{ background: '#f0f4ff', border: '2px solid var(--xen-accent)', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Choose RevTech if…</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, color: '#444' }}>
                <li>Your denial rate is above 8% and you lack visibility into root causes by payer or location</li>
                <li>You are processing significant AR aging and need enterprise-level claim tracking</li>
                <li>You want payer contract benchmarking and underpayment identification at scale</li>
                <li>Payment reconciliation between ERA/EOB postings and your general ledger is a pain point</li>
                <li>You are a multi-location DSO where revenue cycle standardization is a strategic priority</li>
              </ul>
            </div>
          </div>
          <div style={{ marginTop: '2.5rem', padding: '1.75rem 2rem', background: '#fff', border: '1px solid #e0e4f0', borderRadius: '12px' }}>
            <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem' }}>Using the full NDS + RevTech + Xentrios stack</h3>
            <p style={{ color: '#444', lineHeight: 1.75 }}>
              Groups that need both production analytics and revenue cycle intelligence can use the NDS / RevTech / Xentrios
              stack together. InsightFlow covers financial production performance. Xentrios PMS covers scheduling and patient
              engagement at the practice level. DenialIQ, ReconIQ, and PayerIQ cover the RCM layer. The NDS DataVault normalizes
              data across all three systems, so enterprise reporting covers production, claims, and collections in a single
              data warehouse — without requiring a separate analytics vendor.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">FAQ</p>
          <h2>Common questions about RevTech vs. Dental Intelligence</h2>
          <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {jsonLd['@graph'][1].mainEntity.map((faq) => (
              <div key={faq.name} style={{ background: '#f8f9fc', borderRadius: '10px', padding: '1.5rem 2rem' }}>
                <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.5rem', fontSize: '1.05rem' }}>{faq.name}</h3>
                <p style={{ color: '#444', lineHeight: 1.75 }}>{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See how RevTech addresses your revenue cycle challenges.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your current denial rate, AR aging, and payer mix.
            We'll show you exactly how DenialIQ, InsightFlow, and the full RevTech suite map to your group.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
